import React, { useState } from 'react'
import styled from 'styled-components'
import { ActivitiesSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { useActivitiesList } from '../../hooks/useActivities'
import SessionDialog from '../sessions/components/SessionDialog'

const ActivityCard = styled(Card)`
  border-color: ${(p) =>
    p.$done ? p.theme?.color?.accent || '#6B9080' : p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) =>
    p.$done ? p.theme?.color?.accentSoft || '#E1ECE7' : p.theme?.color?.bgRaised || '#fff'};
`

const NoteArea = styled.textarea`
  width: 100%;
  min-height: 84px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  margin-bottom: 12px;
`

const Stars = styled.div`
  display: flex;
  gap: 4px;
`
const Star = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 26px;
  color: ${(p) => (p.$on ? '#E0A526' : 'rgba(0,0,0,0.18)')};
`

function CompleteDialog({ open, activity, onClose, onSubmit }) {
  const [note, setNote] = useState('')
  const [rating, setRating] = useState(0)
  const [busy, setBusy] = useState(false)
  if (!activity) return null
  async function handle() {
    setBusy(true)
    try {
      await onSubmit({ note: note.trim() || null, ratingByPatient: rating || null })
      setNote('')
      setRating(0)
      onClose()
    } finally {
      setBusy(false)
    }
  }
  return (
    <SessionDialog
      open={open}
      title={`Mark "${activity.title}" done`}
      subtitle="Add a quick note or rating — optional."
      primaryLabel="Mark done"
      primaryLoading={busy}
      onPrimary={handle}
      onClose={onClose}
    >
      <NoteArea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="How did it go?"
      />
      <Stars role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} $on={n <= rating} onClick={() => setRating(n)} aria-label={`${n} stars`}>
            ★
          </Star>
        ))}
      </Stars>
    </SessionDialog>
  )
}

export default function ActivitiesPage() {
  const { items, loading, complete } = useActivitiesList()
  const [target, setTarget] = useState(null)

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/care">Back to Care</BackLink>
        <div>
          <Pill>Care</Pill>
          <PageHeading style={{ marginTop: 12 }}>Activities</PageHeading>
          <PageSubheading>Assigned by your practitioners — mark them done as you go.</PageSubheading>
        </div>

        {loading && !items ? (
          <ActivitiesSkeleton />
        ) : !items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No activities assigned.</p>
            <Muted>Practitioner-assigned activities show up here once they&apos;re prescribed.</Muted>
          </div>
        ) : (
          <Stack $gap="12px">
            {items.map((a) => (
              <ActivityCard key={a.id} $done={a.completedToday}>
                <Row $between $gap="12px">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: '#1F2937', marginBottom: 4 }}>
                      {a.title}
                    </div>
                    {a.description && (
                      <Muted style={{ marginBottom: 6 }}>{a.description}</Muted>
                    )}
                    <Muted>
                      {a.frequency || ''}
                      {a.estimatedDurationMinutes ? ` · ${a.estimatedDurationMinutes} min` : ''}
                      {a.prescribedBy ? ` · by ${a.prescribedBy}` : ''}
                    </Muted>
                  </div>
                  {a.completedToday ? (
                    <Pill $tone="success">Done today</Pill>
                  ) : (
                    <GhostButton onClick={() => setTarget(a)}>Mark done</GhostButton>
                  )}
                </Row>
              </ActivityCard>
            ))}
          </Stack>
        )}
      </Stack>

      <CompleteDialog
        open={!!target}
        activity={target}
        onClose={() => setTarget(null)}
        onSubmit={async (body) => {
          await complete(target.id, body)
        }}
      />
    </ContentMaxWidth>
  )
}
